/**
 * Redux reducer for the Batch Tables (Tabular Review) feature.
 *
 * Registered in the store as "batchTables" to avoid collision
 * with the existing "tables" reducer (SSE table-generation).
 */

import { AnyAction } from 'redux';
import { BatchTablesState, TableListItem, TableRow, CellComment } from '../../../types/batchTables';
import {
  BATCH_TABLES_LOADING,
  BATCH_TABLES_FETCH_LIST,
  BATCH_TABLES_APPEND_LIST,
  BATCH_TABLES_SET_DETAIL,
  BATCH_TABLES_LOADING_DETAIL,
  BATCH_TABLES_UPDATE_IN_LIST,
  BATCH_TABLES_REMOVE_FROM_LIST,
  BATCH_TABLES_SET_TEMPLATES,
  BATCH_TABLES_LOADING_TEMPLATES,
  BATCH_TABLES_SET_UPLOADING,
  BATCH_TABLES_SET_PROCESSING,
  BATCH_TABLES_ERROR,
  BATCH_TABLES_UPDATE_CELL,
  BATCH_TABLES_UPDATE_ROW_STATUS,
  BATCH_TABLES_UPDATE_TABLE_STATUS,
  BATCH_TABLES_APPEND_ROWS,
  BATCH_TABLES_SET_UPLOAD_PROGRESS,
  BATCH_TABLES_RESET_COLUMN_CELLS,
  BATCH_TABLE_CREATE_RESET,
  BATCH_TABLE_CREATE_CONNECTING,
  BATCH_TABLE_CREATE_CONNECTED,
  BATCH_TABLE_CREATE_STEPS,
  BATCH_TABLE_CREATE_STEP,
  BATCH_TABLE_CREATE_COMPLETED,
  BATCH_TABLE_CREATE_FAILED,
  BATCH_TABLES_SET_CELL_COMMENTS,
  BATCH_TABLES_LOADING_CELL_COMMENTS,
  BATCH_TABLES_ADD_CELL_COMMENT,
  BATCH_TABLES_UPDATE_CELL_COMMENT,
  BATCH_TABLES_REMOVE_CELL_COMMENT,
  BATCH_TABLES_UPDATE_ROW_DEEP_DIVE,
  BATCH_TABLES_SYNC_PLAYBOOK_START,
  BATCH_TABLES_SYNC_PLAYBOOK_DONE,
  BATCH_TABLES_UPDATE_ROW_JURISDICTION,
} from '../../actions/BatchTables/BatchTablesActions';

type UiStatus = 'Pending' | 'In Progress' | 'Completed' | 'Failed';
const FROM_RAW: Record<string, UiStatus> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  failed: 'Failed',
};

const sseInitial = {
  isConnecting: false,
  connected: false,
  steps: [] as { id: string; label: string; status: UiStatus; messages: string[] }[],
  complete: false,
  ok: null as boolean | null,
  summary: null as string | null,
};

const initialState: BatchTablesState = {
  items: [],
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,

  currentTable: null,
  isLoadingDetail: false,

  templates: [],
  isLoadingTemplates: false,

  isProcessing: false,
  isUploading: false,
  isSyncingPlaybook: false,

  cellComments: [],
  isLoadingComments: false,

  sseCreate: { ...sseInitial },

  uploadProgress: { active: false, tableUuid: null, uploaded: 0, total: 0 },

  error: null,
};

const batchTablesReducer = (state = initialState, action: AnyAction): BatchTablesState => {
  switch (action.type) {
    // ─── List loading ───────────────────────────────
    case BATCH_TABLES_LOADING:
      return { ...state, isLoading: action.payload, error: null };

    case BATCH_TABLES_FETCH_LIST:
      return {
        ...state,
        items: action.payload.items,
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    case BATCH_TABLES_APPEND_LIST:
      return {
        ...state,
        items: [...state.items, ...action.payload.items],
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    // ─── Detail ─────────────────────────────────────
    case BATCH_TABLES_SET_DETAIL: {
      const incoming = action.payload;
      const sameTable = !!(incoming && state.currentTable && state.currentTable.uuid === incoming.uuid);

      // The server status is authoritative - the backend now reflects
      // 'processing' the moment documents are added and 'stopped' the moment a
      // Stop lands, so we no longer pin an optimistic status here (which used to
      // make the UI ignore the real backend state, e.g. stay 'stopped' while the
      // backend had moved on). We DO preserve our optimistic rows while a
      // streaming upload is in flight, so a refetch that predates the
      // just-appended placeholder rows doesn't make them flicker out.
      const uploadingThis = !!(
        incoming &&
        state.uploadProgress.active &&
        state.uploadProgress.tableUuid === incoming.uuid
      );

      if (sameTable && uploadingThis) {
        return {
          ...state,
          currentTable: { ...incoming, rows: state.currentTable!.rows },
        };
      }

      return { ...state, currentTable: incoming };
    }

    case BATCH_TABLES_LOADING_DETAIL:
      return { ...state, isLoadingDetail: action.payload, error: null };

    // ─── Update item in list ────────────────────────
    case BATCH_TABLES_UPDATE_IN_LIST: {
      const updated = action.payload;
      return {
        ...state,
        items: state.items.map((item: TableListItem) =>
          item.uuid === updated.uuid ? { ...item, ...updated } : item
        ),
      };
    }

    // ─── Remove from list ───────────────────────────
    case BATCH_TABLES_REMOVE_FROM_LIST:
      return {
        ...state,
        items: state.items.filter((item: TableListItem) => item.uuid !== action.payload),
        totalItems: Math.max(0, state.totalItems - 1),
      };

    // ─── Templates ──────────────────────────────────
    case BATCH_TABLES_SET_TEMPLATES:
      return { ...state, templates: action.payload };

    case BATCH_TABLES_LOADING_TEMPLATES:
      return { ...state, isLoadingTemplates: action.payload };

    // ─── Upload / Processing flags ──────────────────
    case BATCH_TABLES_SET_UPLOADING:
      return { ...state, isUploading: action.payload };

    case BATCH_TABLES_SET_PROCESSING:
      return { ...state, isProcessing: action.payload };

    // ─── Error ──────────────────────────────────────
    case BATCH_TABLES_ERROR:
      return { ...state, error: action.payload };

    // ─── Real-time updates (WebPubSub) ──────────────
    case BATCH_TABLES_UPDATE_CELL: {
      if (!state.currentTable) return state;
      const { row_id, column_id, cell, cell_id, partial } = action.payload;

      // Partial update path: update specific fields on a cell found by cell_id
      if (cell_id && partial) {
        return {
          ...state,
          currentTable: {
            ...state.currentTable,
            rows: state.currentTable.rows.map((row: TableRow) => {
              const updatedCells = { ...row.cells };
              let changed = false;
              for (const colKey of Object.keys(updatedCells)) {
                if (updatedCells[colKey]?.id === cell_id) {
                  updatedCells[colKey] = { ...updatedCells[colKey], ...partial };
                  changed = true;
                  break;
                }
              }
              return changed ? { ...row, cells: updatedCells } : row;
            }),
          },
        };
      }

      // Full cell replacement path (PubSub live updates).
      // Merge with existing cell data to preserve fields like 'id' that PubSub doesn't include.
      // NOTE: we deliberately do NOT bump cells_completed here. The progress
      // counter is driven solely by the authoritative 'processing_progress'
      // events (see UPDATE_TABLE_STATUS) - mixing an optimistic +1 with the
      // absolute server value made the counter race and jump backward.
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === row_id
              ? {
                  ...row,
                  status: row.status === 'pending' ? 'processing' : row.status,
                  cells: { ...row.cells, [String(column_id)]: { ...row.cells[String(column_id)], ...cell } },
                }
              : row
          ),
        },
      };
    }

    case BATCH_TABLES_UPDATE_ROW_STATUS: {
      if (!state.currentTable) return state;
      const { row_id, status, processing_error } = action.payload;
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === row_id ? { ...row, status, processing_error } : row
          ),
        },
      };
    }

    // Append newly-uploaded rows during a streaming upload (dedup by id) and
    // bump the table's document_count so progress/labels stay consistent.
    case BATCH_TABLES_APPEND_ROWS: {
      if (!state.currentTable) return state;
      const { tableUuid, rows: newRows } = action.payload as { tableUuid: string; rows: TableRow[] };
      if (state.currentTable.uuid !== tableUuid || !newRows?.length) return state;
      const existingIds = new Set(state.currentTable.rows.map((r: TableRow) => r.id));
      const toAdd = newRows.filter((r) => !existingIds.has(r.id));
      if (!toAdd.length) return state;
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: [...state.currentTable.rows, ...toAdd],
          document_count: (state.currentTable.document_count || 0) + toAdd.length,
        },
      };
    }

    case BATCH_TABLES_SET_UPLOAD_PROGRESS: {
      return { ...state, uploadProgress: { ...state.uploadProgress, ...action.payload } };
    }

    case BATCH_TABLES_RESET_COLUMN_CELLS: {
      if (!state.currentTable) return state;
      const { columnId } = action.payload;
      const colKey = String(columnId);
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) => ({
            ...row,
            cells: {
              ...row.cells,
              [colKey]: row.cells[colKey]
                ? { ...row.cells[colKey], status: 'pending', value: null, source_excerpt: null, confidence: null }
                : row.cells[colKey],
            },
          })),
        },
      };
    }

    case BATCH_TABLES_UPDATE_TABLE_STATUS: {
      const { uuid, status, cells_completed, cells_total, fromProgress } = action.payload;
      const ct = state.currentTable;
      // Progress events can arrive slightly out of order; never let the counter
      // jump backward mid-run (an explicit 0 is a (re)start reset). Clamp to total.
      const reconcile = (prev: number, incoming: number | undefined, total: number) => {
        if (incoming == null) return prev;
        const next = incoming === 0 ? 0 : Math.max(prev || 0, incoming);
        return total > 0 ? Math.min(next, total) : next;
      };
      // A progress (count) event must not resurrect a table that's already in a
      // terminal/halted state - in-flight rows keep streaming progress for a
      // while after Stop, and those carry 'processing'. Keep the existing status
      // and only apply the counts in that case.
      const TERMINAL = ['stopped', 'completed', 'partial', 'failed'];
      const statusFor = (prevStatus: string | undefined) =>
        (fromProgress && prevStatus && TERMINAL.includes(prevStatus)) ? prevStatus : status;
      const updatedTable = ct && ct.uuid === uuid
        ? {
            ...ct,
            status: statusFor(ct.status),
            cells_total: cells_total ?? ct.cells_total,
            cells_completed: reconcile(ct.cells_completed, cells_completed, cells_total ?? ct.cells_total),
          }
        : state.currentTable;

      return {
        ...state,
        currentTable: updatedTable,
        items: state.items.map((item: TableListItem) =>
          item.uuid === uuid
            ? {
                ...item,
                status: statusFor(item.status),
                cells_total: cells_total ?? item.cells_total,
                cells_completed: reconcile(item.cells_completed, cells_completed, cells_total ?? item.cells_total),
              }
            : item
        ),
      };
    }

    // ─── SSE Creation Progress ──────────────────────
    case BATCH_TABLE_CREATE_RESET:
      return { ...state, sseCreate: { ...sseInitial } };

    case BATCH_TABLE_CREATE_CONNECTING:
      return { ...state, sseCreate: { ...state.sseCreate, isConnecting: true, connected: false } };

    case BATCH_TABLE_CREATE_CONNECTED:
      return { ...state, sseCreate: { ...state.sseCreate, isConnecting: false, connected: true } };

    case BATCH_TABLE_CREATE_STEPS:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          steps: (action.payload?.steps || []).map((s: any) => ({
            id: String(s.id),
            label: s.name || s.label || `Step ${s.id}`,
            status: FROM_RAW[s.status] || 'Pending',
            messages: [],
          })),
        },
      };

    case BATCH_TABLE_CREATE_STEP: {
      const stepData = action.payload?.step || action.payload || {};
      const { id, status, message, name } = stepData;
      const stepId = String(id);
      const ui = FROM_RAW[status] || 'Pending';
      const steps = [...state.sseCreate.steps];
      const i = steps.findIndex(s => s.id === stepId);
      if (i === -1) {
        steps.push({ id: stepId, label: name || stepId, status: ui, messages: message ? [message] : [] });
      } else {
        steps[i] = {
          ...steps[i],
          status: ui,
          messages: message ? [...steps[i].messages, message] : steps[i].messages,
        };
      }
      return { ...state, sseCreate: { ...state.sseCreate, steps } };
    }

    case BATCH_TABLE_CREATE_COMPLETED:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          complete: true,
          ok: action.payload?.ok ?? true,
          summary: action.payload?.message || 'Table created successfully',
        },
      };

    case BATCH_TABLE_CREATE_FAILED:
      return {
        ...state,
        sseCreate: {
          ...state.sseCreate,
          complete: true,
          ok: false,
          summary: action.payload?.error || 'Failed',
        },
      };

    // ─── Cell Comments ──────────────────────────────
    case BATCH_TABLES_SET_CELL_COMMENTS:
      return { ...state, cellComments: action.payload };

    case BATCH_TABLES_LOADING_CELL_COMMENTS:
      return { ...state, isLoadingComments: action.payload };

    case BATCH_TABLES_ADD_CELL_COMMENT:
      return { ...state, cellComments: [...state.cellComments, action.payload] };

    case BATCH_TABLES_UPDATE_CELL_COMMENT: {
      const updated = action.payload;
      return {
        ...state,
        cellComments: state.cellComments.map((c: CellComment) =>
          c.id === updated.id ? { ...c, ...updated } : c
        ),
      };
    }

    case BATCH_TABLES_REMOVE_CELL_COMMENT:
      return {
        ...state,
        cellComments: state.cellComments.filter((c: CellComment) => c.id !== action.payload.commentId),
      };

    // ─── Deep Dive ──────────────────────────────────────────────────────
    case BATCH_TABLES_UPDATE_ROW_DEEP_DIVE: {
      if (!state.currentTable) return state;
      /* Keyed on the INT id. It read `row_uuid` and matched `row.uuid`, which
         no row has - so the single-row path never updated anything, and
         `bulkCreateDeepDive`, which has always dispatched `row_id`, never
         matched either. Both send `row_id` now. */
      const { row_id, deep_dive_status, review_session_uuid } = action.payload;
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === row_id
              ? { ...row, deep_dive_status, review_session_uuid }
              : row
          ),
        },
      };
    }

    // ─── Playbook Sync ───────────────────────────────────────────────────
    case BATCH_TABLES_SYNC_PLAYBOOK_START:
      return { ...state, isSyncingPlaybook: true };

    case BATCH_TABLES_SYNC_PLAYBOOK_DONE:
      return { ...state, isSyncingPlaybook: false };

    // ─── Row Jurisdiction ────────────────────────────────────────────────
    case BATCH_TABLES_UPDATE_ROW_JURISDICTION: {
      if (!state.currentTable) return state;
      const { rowId, jurisdiction_override } = action.payload;
      return {
        ...state,
        currentTable: {
          ...state.currentTable,
          rows: state.currentTable.rows.map((row: TableRow) =>
            row.id === rowId ? { ...row, jurisdiction_override } : row
          ),
        },
      };
    }

    default:
      return state;
  }
};

export default batchTablesReducer;
